import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { DocumentFlag, DocumentFlagRequest } from '../models/DocumentFlag';
import { AccessTokenStorageService } from './access-token-storage.service';

@Injectable({
  providedIn: 'root'
})
export class FreightDocumentFlagService {
  private flags$: Observable<DocumentFlag[]> = null;
  private flagsOwner: string = null;

  constructor(private httpClient: HttpClient,
              private accessTokenStorage: AccessTokenStorageService) {
  }

  getAll(refresh = false): Observable<DocumentFlag[]> {
    const owner = this.currentOwner();
    if (refresh || !this.flags$ || owner !== this.flagsOwner) {
      this.flagsOwner = owner;
      this.flags$ = this.httpClient.get<DocumentFlag[]>('/portal/companies/me/flags').pipe(
        catchError((error) => {
          this.flags$ = null;
          throw error;
        }),
        shareReplay(1)
      );
    }
    return this.flags$;
  }

  hasActiveFlags(): Observable<boolean> {
    return this.getAll().pipe(
      map((flags) => flags.some((flag) => !flag.archived)),
      catchError(() => of(false))
    );
  }

  create(flag: DocumentFlagRequest): Observable<DocumentFlag> {
    return this.httpClient.post<DocumentFlag>('/portal/companies/me/flags', flag)
      .pipe(tap(() => this.clearCache()));
  }

  update(flagId: number, flag: DocumentFlagRequest): Observable<void> {
    return this.httpClient.put<void>(`/portal/companies/me/flags/${flagId}`, flag)
      .pipe(tap(() => this.clearCache()));
  }

  addToFreightDocument(freightDocumentId: string, flagId: number): Observable<void> {
    return this.httpClient.put<void>(`/freightdocuments/${freightDocumentId}/flags/${flagId}`, null);
  }

  removeFromFreightDocument(freightDocumentId: string, flagId: number): Observable<void> {
    return this.httpClient.delete<void>(`/freightdocuments/${freightDocumentId}/flags/${flagId}`);
  }

  private clearCache(): void {
    this.flags$ = null;
  }

  private currentOwner(): string {
    const token = this.accessTokenStorage.get();
    return token?.accountId ?? token?.access_token ?? null;
  }
}
